import { useState } from "react";
import { MessageSquare, Send, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const TOPICS = [
  "General question",
  "Terms & legal",
  "Business listing",
  "Discover & featured",
  "Boost & payments",
  "Media upload",
  "Report content",
  "Other",
];

export default function ContactModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      setTimeout(() => { setForm({ name: "", email: "", topic: TOPICS[0], message: "" }); setSent(false); }, 300);
    }
    onOpenChange(v);
  };

  const inputCls = "w-full rounded-xl border px-4 py-2.5 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400/40";
  const inputStyle = { backgroundColor: "#f8fafc", borderColor: "#e2e8f0", color: "#0f172a" };
  const labelCls = "block text-xs font-semibold uppercase tracking-wide mb-1.5";
  const labelStyle = { color: "#475569" };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="contact-modal"
        className="border sm:max-w-md shadow-[0_8px_40px_rgba(0,0,0,0.18)]"
        style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0", color: "#0f172a" }}
      >
        <DialogHeader>
          <DialogTitle
            className="text-xl font-bold flex items-center gap-2"
            style={{ color: "#0f172a" }}
          >
            <MessageSquare size={18} style={{ color: "#10b981" }} />
            Contact HighRank
          </DialogTitle>
          <DialogDescription style={{ color: "#64748b", fontSize: "13px" }}>
            We'll reply to your email — usually within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#d1fae5" }}
            >
              <Check size={28} style={{ color: "#059669" }} />
            </div>
            <div>
              <p className="font-bold text-base" style={{ color: "#0f172a" }}>Message sent!</p>
              <p className="text-sm mt-1" style={{ color: "#64748b" }}>
                Check your inbox for a confirmation. We'll be in touch shortly.
              </p>
            </div>
            <button
              onClick={() => handleClose(false)}
              className="mt-2 rounded-full px-6 py-2.5 text-sm font-bold transition-all hover:opacity-90"
              style={{ backgroundColor: "#10b981", color: "#ffffff" }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls} style={labelStyle}>Name</label>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                  style={inputStyle}
                  required
                />
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Email</label>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                  style={inputStyle}
                  required
                />
              </div>
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Topic</label>
              <select
                data-testid="contact-topic"
                value={form.topic}
                onChange={set("topic")}
                className={inputCls}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {TOPICS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls} style={labelStyle}>Message</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us what's on your mind…"
                rows={4}
                className={inputCls}
                style={{ ...inputStyle, resize: "vertical", minHeight: "96px" }}
                required
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "#10b981", color: "#ffffff" }}
            >
              <Send size={14} />
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
